import * as dayjs from 'dayjs';
import { ISchedule } from '../../contracts/schedule.js';
import { MapUtils } from '../../utility/map-utils.js';
import { TimeZoneRegion } from './time-zone-region.js';
import { TimeZone } from '../../time/timezone.js';
import { DataModel, DataModelBuilder, isSameData,
  isSameList, SerializationContext, SerializedData } from '../dto/dto.js';
import { DayOfYear } from './day-of-year.js';
import { ScheduleTimeSpan } from './schedule-time-span.js';

export class DailySchedule extends DataModel implements ISchedule {
  static readonly TYPE_ID: string = '475589a04fc2461fab74d28707cd5322';

  public readonly uniqueId: string;
  public readonly localDate: dayjs.Dayjs;

  private constructor(
    public readonly dayOfYear: DayOfYear,
    public readonly timezone: TimeZoneRegion,
    public readonly scheduleTemplateId: string | null,
    public readonly scheduleTemplateName: string | null,
    public readonly times: ScheduleTimeSpan[],
    public readonly closed: boolean,
  ) {
    super();

    this.uniqueId = DailySchedule.getUniqueId(dayOfYear);
    this.localDate = this.dayOfYear.toDate(TimeZone.ofRegion(timezone));
  }

  get timestamp(): number {
    return this.localDate.valueOf();
  }

  get hasAnyHoursDefined(): boolean {
    return !this.closed && this.times.length > 0;
  }

  get $typeId(): string {
    return DailySchedule.TYPE_ID;
  }

  static closed(timezone: TimeZoneRegion, date: dayjs.Dayjs): DailySchedule {
    return new DailySchedule(
        DayOfYear.fromDate(date),
        timezone,
        null,
        null,
        [],
        true,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): DailySchedule {
    return new DailySchedule(
        context.fromData<DayOfYear>(MapUtils.getRaw(map, 'dayOfYear') as SerializedData),
        MapUtils.get<TimeZoneRegion>(map, 'timezone'),
        MapUtils.getOrNull<string>(map, 'scheduleTemplateId'),
        MapUtils.getOrNull<string>(map, 'scheduleTemplateName'),
        MapUtils.getList<ScheduleTimeSpan>(map, 'times', (value) =>
          context.fromData<ScheduleTimeSpan>(value as SerializedData),
        ),
        MapUtils.get<boolean>(map, 'closed'),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'dayOfYear': context.toData(this.dayOfYear),
      'timezone': this.timezone,
      'times': this.times.map((t) => context.toData(t)),
      'closed': this.closed,
      'scheduleTemplateId': this.scheduleTemplateId,
      'scheduleTemplateName': this.scheduleTemplateName,
    };
  }

  clone(): this {
    return new DailySchedule(
        this.dayOfYear.clone(),
        this.timezone,
        this.scheduleTemplateId,
        this.scheduleTemplateName,
        this.times.map((t) => t.clone()),
        this.closed,
    ) as this;
  }

  isSame(other: DailySchedule): boolean {
    if(this === other) {
      return true;
    }

    return isSameData(other.dayOfYear, this.dayOfYear) &&
        isSameList(other.times, this.times, isSameData) &&
        other.timezone === this.timezone &&
        other.closed === this.closed &&
        other.scheduleTemplateId === this.scheduleTemplateId &&
        other.scheduleTemplateName === this.scheduleTemplateName;
  }

  static getUniqueId(dayOfYear: DayOfYear): string {
    return `${dayOfYear.year}.${dayOfYear.month.toString().padStart(2, '0')}` +
    `.${dayOfYear.day.toString().padStart(2, '0')}`;
  }
}

export class DailyScheduleBuilder extends DataModelBuilder<DailySchedule> {
  build(context: SerializationContext, map: SerializedData): DailySchedule {
    return DailySchedule.fromMap(context, map);
  }
}
